<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Text from '@uiKit/elements/Text.vue'

interface Props {
  visible: boolean
  downloading?: boolean
  error?: string
}

defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:visible', val: boolean): void
  (e: 'export-csv'): void
  (e: 'export-excel'): void
}>()

const close = () => emit('update:visible', false)
</script>

<template>
  <Teleport to="body">
    <Transition name="tep-fade">
      <Box v-if="visible" class="tep-backdrop" @click="close" />
    </Transition>

    <Transition name="tep-slide">
      <Box
        v-if="visible"
        align="center"
        justify="center"
        class="tep-wrap"
        role="dialog"
        aria-modal="true"
      >
        <Box direction="col" gap="6" class="tep-panel">
          <Box
            is="button"
            flex
            align="center"
            justify="center"
            class="tep__close"
            type="button"
            aria-label="Close"
            @click="close"
          >
            <PiIcon name="times" />
          </Box>

          <Box direction="col" align="center" gap="4" class="tep__header">
            <Box flex align="center" justify="center" class="tep__header-icon">
              <PiIcon name="arrows-h" />
            </Box>
            <Box direction="col" align="center" gap="3" class="tep__header-text">
              <Text type="title-lg">Export Transactions</Text>
              <Text type="caption" align="center" is="p" class="tep__subtitle">
                Exports all transactions matching current filters.<br />
                <Text is="strong" type="caption" weight="bold" color="default">File name:</Text>
                Sliceal_Transaction_Statement_YYYYMMDD
              </Text>
            </Box>
          </Box>

          <Box v-if="error" align="center" gap="2" class="tep__error">
            <PiIcon name="exclamation-triangle" class="tep__error-icon" />
            <Text type="caption" is="span">{{ error }}</Text>
          </Box>

          <Box direction="col" gap="2" class="tep__actions">
            <Box
              is="button"
              align="center"
              gap="3"
              class="tep__action"
              :class="{ 'tep__action--disabled': downloading }"
              type="button"
              aria-label="Export as CSV"
              :disabled="downloading"
              @click="emit('export-csv')"
            >
              <Box flex align="center" justify="center" class="tep__icon-box tep__icon-box--csv">
                <PiIcon name="table" />
              </Box>
              <Box direction="col" gap="1" class="tep__action-info">
                <Text type="subheading">Export as CSV</Text>
                <Text type="caption">Spreadsheet format · All columns included</Text>
              </Box>
              <PiIcon v-if="downloading" name="spin" class="tep__spinner" />
            </Box>

            <Box
              is="button"
              align="center"
              gap="3"
              class="tep__action"
              :class="{ 'tep__action--disabled': downloading }"
              type="button"
              aria-label="Export as Excel"
              :disabled="downloading"
              @click="emit('export-excel')"
            >
              <Box flex align="center" justify="center" class="tep__icon-box tep__icon-box--pdf">
                <PiIcon name="file-excel" />
              </Box>
              <Box direction="col" gap="1" class="tep__action-info">
                <Text type="subheading">Export as Excel</Text>
                <Text type="caption">Spreadsheet workbook · All data included</Text>
              </Box>
              <PiIcon v-if="downloading" name="spin" class="tep__spinner" />
            </Box>
          </Box>
        </Box>
      </Box>
    </Transition>
  </Teleport>
</template>

<style scoped>
.tep-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(16, 24, 40, 0.5);
  z-index: 1000;
}

.tep-wrap {
  position: fixed;
  inset: 0;
  z-index: 1001;
  padding: 1rem;
  pointer-events: none;
}

.tep-panel {
  background: var(--p-surface-0);
  border: 1px solid var(--p-primary-200);
  border-radius: var(--p-border-radius-lg);
  padding: 1.25rem;
  width: 100%;
  max-width: 385px;
  box-sizing: border-box;
  box-shadow: 0 20px 60px rgba(10, 13, 18, 0.18);
  pointer-events: all;
  position: relative;
}

.tep__close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  color: var(--p-text-muted-color);
  transition:
    background 0.15s,
    color 0.15s;
}

.tep__close:hover {
  background: var(--p-surface-100);
  color: var(--p-text-color);
}

.tep__close :deep(.pi) {
  font-size: 16px;
}

.tep__header-icon {
  width: 60px;
  height: 60px;
  background: var(--p-primary-50);
  border-radius: 14px;
}

.tep__header-icon :deep(.pi) {
  font-size: 26px;
  color: var(--p-primary-600);
}

.tep__subtitle {
  white-space: normal;
  line-height: 1.6;
  margin: 0;
}

.tep__error {
  padding: 0.625rem 0.875rem;
  background: var(--p-danger-50);
  border: 1px solid var(--p-danger-200);
  border-radius: 8px;
}

.tep__error-icon {
  flex-shrink: 0;
  color: var(--p-danger-500);
  font-size: 14px;
}

.tep__error :deep(.text--caption) {
  color: var(--p-danger-700);
}

.tep__action {
  padding: 17px 21px;
  background: var(--p-surface-0);
  border: 1px solid var(--p-surface-300);
  border-radius: var(--p-border-radius-lg);
  cursor: pointer;
  width: 100%;
  text-align: left;
  transition:
    border-color 0.15s,
    background 0.15s;
}

.tep__action:focus-visible {
  outline: 2px solid var(--p-primary-600);
  outline-offset: 2px;
}

.tep__action:hover:not(:disabled) {
  background: var(--p-surface-50);
  border-color: var(--p-primary-200);
}

.tep__action--disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.tep__spinner {
  margin-left: auto;
  flex-shrink: 0;
  animation: tep-spin 0.8s linear infinite;
  color: var(--p-surface-400);
}

@keyframes tep-spin {
  to {
    transform: rotate(360deg);
  }
}

.tep__icon-box {
  width: 46px;
  height: 46px;
  border-radius: 10px;
  flex-shrink: 0;
}

.tep__icon-box--csv {
  background: var(--p-success-100);
}

.tep__icon-box--csv :deep(.pi) {
  font-size: 20px;
  color: var(--p-success-600);
}

.tep__icon-box--pdf {
  background: var(--p-primary-50);
}

.tep__icon-box--pdf :deep(.pi) {
  font-size: 20px;
  color: var(--p-primary-600);
}

.tep-fade-enter-active,
.tep-fade-leave-active {
  transition: opacity 0.22s ease;
}

.tep-fade-enter-from,
.tep-fade-leave-to {
  opacity: 0;
}

.tep-slide-enter-active,
.tep-slide-leave-active {
  transition:
    opacity 0.22s ease,
    transform 0.22s ease;
}

.tep-slide-enter-from,
.tep-slide-leave-to {
  opacity: 0;
  transform: translateY(16px) scale(0.97);
}
</style>
